import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { questions } from "@/data/questions";
import { interstitials } from "@/data/interstitials";
import { CheckCircle2, XCircle, AlertTriangle } from "lucide-react";

export const AdminScoringDetails = () => {
  const interstitialIds = new Set<number>(Object.keys(interstitials).map(Number));
  
  // Analizuj pytania scoringowe
  const scoringQuestions = questions.filter(q => 
    q.type !== 'text' && 
    !q.isDynamic && 
    !interstitialIds.has(q.id)
  );

  // Policz maksymalny możliwy wynik
  let maxPossible = 0;
  scoringQuestions.forEach(question => {
    if (question.type === 'single') {
      const maxOpt = Math.max(...question.options.map(o => o.score));
      maxPossible += isFinite(maxOpt) ? maxOpt : 0;
    } else if (question.type === 'multiple') {
      maxPossible += question.options
        .filter(o => o.score > 0)
        .reduce((s, o) => s + o.score, 0);
    }
  });

  // Grupuj pytania według typu
  const singleChoiceQuestions = scoringQuestions.filter(q => q.type === 'single');
  const multipleChoiceQuestions = scoringQuestions.filter(q => q.type === 'multiple');

  const excludedQuestions = questions.filter(q => 
    q.type === 'text' || 
    q.isDynamic || 
    interstitialIds.has(q.id)
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">Szczegóły Punktacji Quizu</h2>
        <p className="text-muted-foreground">
          Analiza systemu scoringu i punktacji pytań
        </p>
      </div>

      {/* Podsumowanie */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Pytania scoringowe</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{scoringQuestions.length}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Wpływają na wynik końcowy
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Max możliwy score</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{maxPossible.toFixed(1)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Suma wszystkich punktów
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Pytania wykluczone</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{excludedQuestions.length}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Interstitiale, text, dynamic
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Normalizacja</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">1-10</div>
            <p className="text-xs text-muted-foreground mt-1">
              Skala końcowa wyniku
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Pytania Single Choice */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            Pytania Single Choice ({singleChoiceQuestions.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {singleChoiceQuestions.map((q) => {
              const maxScore = Math.max(...q.options.map(o => o.score));
              const minScore = Math.min(...q.options.map(o => o.score));
              
              return (
                <div key={q.id} className="p-4 border rounded-lg space-y-2">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">Q{q.id}</Badge>
                        <span className="font-medium">{q.question}</span>
                      </div>
                      <div className="mt-2 flex items-center gap-4 text-sm">
                        <span className="text-muted-foreground">
                          Min: <span className="font-medium">{minScore}</span>
                        </span>
                        <span className="text-muted-foreground">
                          Max: <span className="font-bold text-green-600">{maxScore}</span>
                        </span>
                        <span className="text-muted-foreground">
                          Opcji: {q.options.length}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 mt-3 text-sm">
                    {q.options.map((opt) => (
                      <div 
                        key={opt.value} 
                        className={`p-2 rounded flex items-center justify-between ${
                          opt.score === maxScore 
                            ? 'bg-green-50 border border-green-200' 
                            : 'bg-muted/50'
                        }`}
                      >
                        <span className="truncate">{opt.label}</span>
                        <Badge variant={opt.score === maxScore ? "default" : "secondary"}>
                          {opt.score}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Pytania Multiple Choice */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-blue-600" />
            Pytania Multiple Choice ({multipleChoiceQuestions.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {multipleChoiceQuestions.map((q) => {
              const maxPossible = q.options
                .filter(o => o.score > 0)
                .reduce((s, o) => s + o.score, 0);
              
              return (
                <div key={q.id} className="p-4 border rounded-lg space-y-2">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">Q{q.id}</Badge>
                        <span className="font-medium">{q.question}</span>
                      </div>
                      <div className="mt-2 flex items-center gap-4 text-sm">
                        <span className="text-muted-foreground">
                          Max możliwy: <span className="font-bold text-blue-600">{maxPossible.toFixed(1)}</span>
                        </span>
                        <span className="text-muted-foreground">
                          Opcji: {q.options.length}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 mt-3 text-sm">
                    {q.options.map((opt) => (
                      <div 
                        key={opt.value} 
                        className={`p-2 rounded flex items-center justify-between ${
                          opt.score > 0 
                            ? 'bg-blue-50 border border-blue-200' 
                            : 'bg-muted/50'
                        }`}
                      >
                        <span className="truncate">{opt.label}</span>
                        <Badge variant={opt.score > 0 ? "default" : "secondary"}>
                          {opt.score}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Pytania wykluczone */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-orange-600" />
            Pytania Wykluczone ze Scoringu ({excludedQuestions.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {excludedQuestions.map((q) => {
              let reason = "";
              if (q.type === 'text') reason = "Pytanie tekstowe";
              else if (q.isDynamic) reason = "Pytanie dynamiczne (Q20)";
              else if (interstitialIds.has(q.id)) reason = "Przerywnik (interstitial)";
              
              return (
                <div key={q.id} className="p-3 border rounded-lg flex items-center justify-between bg-orange-50">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">Q{q.id}</Badge>
                    <span className="font-medium">{q.question}</span>
                  </div>
                  <Badge variant="secondary">{reason}</Badge>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Wyjaśnienie normalizacji */}
      <Card className="bg-blue-50 border-blue-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-blue-600" />
            Jak działa normalizacja?
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <p>
            <strong>1. Zlicz surowe punkty:</strong> Suma punktów z odpowiedzi użytkownika (totalScore)
          </p>
          <p>
            <strong>2. Oblicz max możliwy:</strong> Suma maksymalnych punktów z widocznych pytań (maxPossible = {maxPossible.toFixed(1)})
          </p>
          <p>
            <strong>3. Normalizuj do skali 1-10:</strong> (totalScore / maxPossible) × 10, zaokrąglone do 1 miejsca
          </p>
          <p>
            <strong>4. Zastosuj limity:</strong> Wynik zawsze między 1.0 a 10.0
          </p>
          <div className="mt-4 p-3 bg-white rounded border border-blue-300">
            <p className="font-mono text-xs">
              score = Math.max(1, Math.min(10, (totalScore / {maxPossible.toFixed(1)}) * 10))
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
